import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { CrewMember, CrewStatus } from "@/mocks/admin/crew";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (member: CrewMember) => void;
};

const roleOptions = ["Lead Mover", "Mover", "Driver", "Mover / Driver"];
const statusOptions: { key: CrewStatus; label: string }[] = [
  { key: "active", label: "Active" },
  { key: "training", label: "Training" },
  { key: "off", label: "Off" },
  { key: "on-leave", label: "On Leave" },
];

const skillOptions = [
  "Piano",
  "Antiques",
  "Packing",
  "Heavy lift",
  "CDL-A",
  "CDL-B",
  "CDL-C",
  "Long Distance",
  "Commercial",
  "Office",
  "Electronics",
  "Inventory",
  "Lead",
];

const DAYS: ("M" | "T" | "W" | "Th" | "F" | "Sa" | "Su")[] = [
  "M",
  "T",
  "W",
  "Th",
  "F",
  "Sa",
  "Su",
];

const dayLabels: Record<string, string> = {
  M: "Mon",
  T: "Tue",
  W: "Wed",
  Th: "Thu",
  F: "Fri",
  Sa: "Sat",
  Su: "Sun",
};

const avatarColors = [
  "bg-brand-500",
  "bg-indigo-500",
  "bg-pink-500",
  "bg-emerald-600",
  "bg-fuchsia-500",
  "bg-amber-600",
  "bg-rose-500",
  "bg-teal-600",
  "bg-cyan-600",
  "bg-orange-600",
  "bg-violet-500",
  "bg-lime-600",
];

function generateAccessToken() {
  // 32-char hex token, browser-friendly
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

function pickInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatHireDate(iso: string) {
  if (!iso) return "";
  const d = new Date(iso + "-01");
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

export default function AddCrewModal({ open, onClose, onCreate }: Props) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [role, setRole] = useState(roleOptions[1]);
  const [hireDate, setHireDate] = useState("");
  const [hourlyRate, setHourlyRate] = useState<string>("24");
  const [weeklyTarget, setWeeklyTarget] = useState<string>("40");
  const [status, setStatus] = useState<CrewStatus>("active");
  const [skills, setSkills] = useState<string[]>(["Packing"]);
  const [availability, setAvailability] = useState<string[]>(["M", "T", "W", "Th", "F"]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setError("");
      setSubmitting(false);
      setName("");
      setEmail("");
      setPhone("");
      setCity("");
      setRole(roleOptions[1]);
      setHireDate("");
      setHourlyRate("24");
      setWeeklyTarget("40");
      setStatus("active");
      setSkills(["Packing"]);
      setAvailability(["M", "T", "W", "Th", "F"]);
    }
  }, [open]);

  if (!open) return null;

  const toggleSkill = (s: string) => {
    setSkills((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  };

  const toggleDay = (d: string) => {
    setAvailability((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));
  };

  const canNext1 = name.trim() && phone.trim() && city.trim();
  const canNext2 = role && hireDate && hourlyRate;

  const handleSubmit = async () => {
    setError("");
    const rate = Number(hourlyRate);
    const target = Number(weeklyTarget);
    if (!hourlyRate || Number.isNaN(rate) || rate < 0) {
      setError("Please enter a valid hourly rate.");
      return;
    }
    if (!weeklyTarget || Number.isNaN(target) || target <= 0) {
      setError("Please enter a valid weekly target (hours).");
      return;
    }
    if (skills.length === 0) {
      setError("Add at least one skill.");
      return;
    }

    setSubmitting(true);

    const newId = "C-" + (200 + Math.floor(Math.random() * 800));
    const initials = pickInitials(name);
    const color = avatarColors[Math.floor(Math.random() * avatarColors.length)];
    const hireDateFormatted = formatHireDate(hireDate);
    const accessToken = generateAccessToken();

    const { error: insertError } = await supabase.from("crew_members").insert({
      id: newId,
      name: name.trim(),
      initials,
      color,
      role,
      email: email.trim() || null,
      phone: phone.trim(),
      hire_date: hireDateFormatted,
      rating: 0,
      completed_moves: 0,
      hourly_rate: rate,
      weekly_hours: 0,
      weekly_target: target,
      status,
      skills,
      availability,
      city: city.trim(),
      access_token: accessToken,
    });

    if (insertError) {
      setError(insertError.message);
      setSubmitting(false);
      return;
    }

    const newMember: CrewMember = {
      id: newId,
      name: name.trim(),
      initials,
      color,
      role,
      email: email.trim(),
      phone: phone.trim(),
      hireDate: hireDateFormatted,
      rating: 0,
      completedMoves: 0,
      hourlyRate: rate,
      weeklyHours: 0,
      weeklyTarget: target,
      status,
      skills,
      availability: availability as CrewMember["availability"],
      city: city.trim(),
      accessToken,
    };

    onCreate(newMember);
    setSubmitting(false);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white w-full max-w-2xl rounded-lg border border-ink-100 max-h-[90vh] flex flex-col pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                <i className="ri-user-add-line text-lg"></i>
              </span>
              <div>
                <h2 className="font-display text-lg font-bold text-ink-900">Add Crew Member</h2>
                <div className="text-xs text-ink-500">
                  Step {step} of 3 ·{" "}
                  {step === 1
                    ? "Personal details"
                    : step === 2
                    ? "Role & pay"
                    : "Skills & availability"}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="px-6 pt-4 pb-2 shrink-0">
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    s <= step ? "bg-emerald-500" : "bg-cream-100"
                  }`}
                ></div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-5">
            {step === 1 && (
              <div className="space-y-4">
                <Field label="Full name" required>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Jamal Pearson"
                    className="form-input"
                  />
                  {name && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">
                        {pickInitials(name)}
                      </div>
                      <span className="text-xs text-ink-500">
                        Avatar initials will look like this
                      </span>
                    </div>
                  )}
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Phone" required>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="(512) 555-0142"
                      className="form-input"
                    />
                  </Field>
                  <Field label="Work email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@haulmark.co"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Home city" required>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-ink-400">
                      <i className="ri-map-pin-line text-sm"></i>
                    </span>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="e.g. Austin, TX"
                      className="form-input pl-9"
                    />
                  </div>
                </Field>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <Field label="Role" required>
                  <div className="grid grid-cols-2 gap-2">
                    {roleOptions.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRole(r)}
                        className={`text-sm font-semibold px-3 py-2.5 rounded-md border cursor-pointer whitespace-nowrap transition-colors ${
                          role === r
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Hire date" required>
                  <input
                    type="month"
                    value={hireDate}
                    onChange={(e) => setHireDate(e.target.value)}
                    className="form-input"
                  />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Hourly rate ($)" required>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
                        $
                      </span>
                      <input
                        type="number"
                        min={0}
                        step={1}
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(e.target.value)}
                        placeholder="24"
                        className="form-input pl-7"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 text-xs">
                        /hr
                      </span>
                    </div>
                  </Field>
                  <Field label="Weekly target (hours)" required>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={weeklyTarget}
                      onChange={(e) => setWeeklyTarget(e.target.value)}
                      placeholder="40"
                      className="form-input"
                    />
                  </Field>
                </div>

                <Field label="Status">
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setStatus(s.key)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                          status === s.key
                            ? "bg-ink-900 text-white border-ink-900"
                            : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5">
                <Field label="Skills">
                  <div className="text-[11px] text-ink-500 mb-2">
                    Tap to toggle — pick everything they're qualified for.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {skillOptions.map((s) => {
                      const active = skills.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleSkill(s)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border cursor-pointer whitespace-nowrap transition-colors ${
                            active
                              ? "bg-emerald-500 text-white border-emerald-500"
                              : "bg-white text-ink-700 border-ink-200 hover:bg-cream-100"
                          }`}
                        >
                          {active && <i className="ri-check-line mr-1"></i>}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <Field label="Available days">
                  <div className="text-[11px] text-ink-500 mb-2">
                    Which days of the week they can usually work.
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {DAYS.map((d) => {
                      const active = availability.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleDay(d)}
                          className={`w-12 h-10 rounded-md text-xs font-bold cursor-pointer transition-colors ${
                            active
                              ? "bg-brand-100 text-brand-700 border border-brand-300"
                              : "bg-cream-50 text-ink-400 border border-ink-100 hover:bg-cream-100"
                          }`}
                        >
                          {dayLabels[d]}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {error && (
                  <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2 rounded-md">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{error}</span>
                  </div>
                )}

                {/* Preview card */}
                <div className="bg-cream-50 border border-ink-100 rounded-md p-4">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-ink-500 mb-2">
                    Preview
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-emerald-500 text-white text-sm font-bold flex items-center justify-center shrink-0">
                      {pickInitials(name || "?")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink-900 truncate">
                        {name || "New Crew Member"}
                      </div>
                      <div className="text-[11px] text-ink-500">
                        {role} · ${hourlyRate || 0}/hr · {weeklyTarget || 40}h target ·{" "}
                        {skills.length} skill{skills.length === 1 ? "" : "s"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-ink-100 bg-cream-50 flex items-center justify-between gap-2 shrink-0 rounded-b-lg">
            <button
              onClick={onClose}
              className="text-sm font-semibold text-ink-500 hover:text-ink-900 px-3 py-2 cursor-pointer"
            >
              Cancel
            </button>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-white cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !canNext1 : !canNext2}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Adding…
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line"></i>
                      Add to Roster
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider text-ink-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}